import { makeMnErrorView } from "common/components/error";
import { FetchErrorRetry } from "common/helpers/errors";
import { _ } from "extensions/underscore";

import { getters } from "common/components/getters/getters";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";

import { FETCH_SUMMARY_DATA } from "common/actions/dashboard";

import { DefaultView } from "../defaultView";
import { TrendlineBodyContents } from "./trendlineBodyContents";
import trendlineWidgetTemplate from "./trendlineView.jst";
import { getDateRangesFromQueryParams } from "../widgetHelper";

export const TrendlineView = DefaultView.extend({
    type: "trendline",
    updateNote: false,
    template: trendlineWidgetTemplate,
    regions: {
        tabs: ".trendline-tabs"
    },
    
    events: {
        "click @ui.widgetTitle, .trendline-data": "drilldownToCorrespondingPage",
        ...DefaultView.prototype.events
    },
    
    ui: {
        widgetTitle: ".trendline-name",
        content: ".trendline-widget-body",
        ...DefaultView.prototype.ui
    },
    
    attributes() {
        return {
            ...DefaultView.prototype.attributes.apply(this, arguments),
            "gs-max-h": 6,
            "gs-min-h": 3,
            "gs-min-w": 4,
            "gs-max-w": 12
        };
    },
    
    setDataCollection(...args) {
        const widgetCollectionParams = this.getOption("parent").getViewCollection({
            url: _.result(this, "getAPIEndpoint"),
            params: this.getRequestParams(),
            mode: this.getCollectionSortMode()
        });
        
        this.collection = widgetCollectionParams.collection;
        this.collection.queryParams.q = this.mergeFilters();
        this.skipReactOnChanges = !widgetCollectionParams.isNew;

        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }

        this.listenTo(this.collection, "before:fetched", () => {
            this.addSpinners();
        });

        this.listenTo(this.collection, "fetched", this.makeContent.bind(this));

        this.collection.fetch = function() {
            this.trigger("before:fetched");
            this.trigger("fetched");
        };

        if (this.skipReactOnChanges) {
            this.addSpinners();
        }
    },

    makeContent() {
        const first_pivot = "provider";
        const first_pivot_key = getters.findPivotKey(first_pivot);

        // TODO get this from model
        const queryParams = this.collection.queryParams;
        const { start, end } = queryParams;
        // Put things in the same format as the compare function so in future easily incorporated
        const widgetQualifiers = new Qualifiers({ value: this.filterInfo.mergedString });
        const endpoint = this.model.subtypeModel.get("api_endpoint");

        this.existingPromiseRequest = FETCH_SUMMARY_DATA({
            url: _.isFunction(endpoint) ? endpoint(first_pivot_key) : endpoint,
            start,
            end,
            q: widgetQualifiers
        }).then((data) => {
            // While this does not fix the race condition, it should at least
            // prevent the unhandled exception.
            if (this.ui.content?.find) {
                this.removeSpinners();
            }

            const options = {
                data,
                dateRanges: getDateRangesFromQueryParams(queryParams),
                first_pivot_key,
                widgetContainer: this.el,
                widgetType: this.model.get("widgetType"),
                widgetSubType: this.model.get("widgetSubtype"),
                widgetQualifiers,
                subModel: this.model.subtypeModel
            };

            this.showChildView("tabs", <TrendlineBodyContents {...options}/>, {
                waitForAttach: true,
                dumb: true
            });
        }, (error) => {
            if (!this.isDestroyed()) {
                if (this.ui.content?.find) {
                    this.removeSpinners();
                }

                this.showChildView("tabs", makeMnErrorView(new FetchErrorRetry({
                    message: "Please refresh widget as data could not load",
                    customActions: [{
                        className: "retry-widget",
                        label: "Refresh widget"
                    }],
                    hideDetails: true,
                    custom: true,
                    originalError: error
                }), true, false));
            }
        });
    },
    
    getLoadingChildren() {
        return this.ui.content.find(".widget-body");
    },
    
    makeWidgetTitle() {
        if (this.model.get("options").by) {
            return `${this.model.get("title")} (${this.model.get("options").by})`;
        }
    
        return this.model.get("title");
    },
    
    // Called from parent gridstack:init
    onGridstackInit() {
        this.addWidgetTitle();
        this.loadData();
    },
    
    onDestroy() {
        DefaultView.prototype.onDestroy.call(this);
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    },
    
    refetchWidgetData(e) {
        e.preventDefault();
        e.stopPropagation();
        // this.listenTo(this.collection, "fetched", () => {
        //     this.handleSuccess();
        // });
        this.setDataCollection();
    },
    
    showErrorMessage() {
        this.addWidgetTitle();
        if (this.getChildView("chart")) {
            this.showChildView("chart", makeMnErrorView(new FetchErrorRetry({
                message: "Please refresh widget as data could not load",
                customActions: [{
                    className: "retry-widget",
                    label: "Refresh widget"
                }],
                hideDetails: true,
                custom: true
            }), true, false));
        }
    },
    
    getClosestTickToClickedPoint(event) {
        const ticks = _.map(event.xAxis[0].axis.ticks, (v, k) => Number.parseInt(k, 10));
        
        return _.getClosestIntegerInSortedArray(ticks, Number.parseInt(event.xAxis[0].value, 10));
    }
    
});
